import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Select,
  Upload,
  InputNumber,
  message,
  Row,
  Col,
  Divider,
  Typography,
  Spin,
  Space,
} from 'antd';
import { UploadOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { fetchStoreById, updateStore } from '../store/slices/storesSlice';
import { categoryService } from '../services/categoryService';
import { uploadService } from '../services/uploadService';
import type { Category } from '../types';
import PageShell from '../components/Layout/PageShell';

const { Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const EditStore: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [logoUrl, setLogoUrl] = useState<string>('');
  const { currentStore, loading: storeLoading } = useAppSelector((state) => state.stores);

  // Load store data and categories
  useEffect(() => {
    if (id) {
      dispatch(fetchStoreById(id));
    }
    loadCategories();
  }, [dispatch, id]);

  // Set form values when store data is loaded
  useEffect(() => {
    if (currentStore) {
      form.setFieldsValue({
        storeName: currentStore.storeName,
        description: currentStore.description,
        tagline: currentStore.tagline,
        categories: currentStore.categories?.map((cat: any) => cat._id || cat),
        businessInfo: {
          businessType: currentStore.businessInfo?.businessType,
          phone: currentStore.businessInfo?.phone,
          email: currentStore.businessInfo?.email,
          website: currentStore.businessInfo?.website,
          yearEstablished: currentStore.businessInfo?.yearEstablished,
        },
        address: {
          street: currentStore.address?.street,
          city: currentStore.address?.city,
          state: currentStore.address?.state,
          zipCode: currentStore.address?.zipCode,
          country: currentStore.address?.country,
        },
        settings: {
          isActive: currentStore.settings?.isActive,
          serviceRadius: currentStore.settings?.serviceRadius,
          autoRespondToLeads: currentStore.settings?.autoRespondToLeads,
          autoResponseMessage: currentStore.settings?.autoResponseMessage,
        }
      });
      
      // Set existing logo URL if available
      if (currentStore.logo) {
        setLogoUrl(currentStore.logo);
      }
    }
  }, [currentStore, form]);

  const loadCategories = async () => {
    try {
      setCategoriesLoading(true);
      const response = await categoryService.getAllActiveCategories();
      setCategories(response);
    } catch (error) {
      console.error('Error loading categories:', error);
      message.error('Failed to load categories');
    } finally {
      setCategoriesLoading(false);
    }
  };

  const handleLogoUpload = async (file: File) => {
    try {
      // Validate file
      if (!uploadService.validateImageFile(file)) {
        message.error('Please upload a valid image file (JPEG, PNG, WebP)');
        return false;
      }

      if (!uploadService.validateFileSize(file)) {
        message.error('File size must be less than 10MB');
        return false;
      }

      const result = await uploadService.uploadImage(file);
      setLogoUrl(result.url);
      message.success('Logo uploaded successfully');
      return false; // Prevent default upload behavior
    } catch (error: any) {
      message.error(error.message || 'Failed to upload logo');
      return false;
    }
  };

  const onFinish = async (values: any) => {
    if (!id) return;
    
    setLoading(true);
    try {
      console.log('Updating store with data:', values);
      
      // Prepare store data for API
      const storeData = {
        storeName: values.storeName,
        description: values.description,
        tagline: values.tagline,
        categories: values.categories,
        logo: logoUrl, // Include uploaded logo URL
        businessInfo: {
          businessType: values.businessInfo?.businessType,
          phone: values.businessInfo?.phone,
          email: values.businessInfo?.email,
          website: values.businessInfo?.website,
          yearEstablished: values.businessInfo?.yearEstablished,
        },
        address: {
          street: values.address?.street,
          city: values.address?.city,
          state: values.address?.state,
          zipCode: values.address?.zipCode,
          country: values.address?.country,
        },
        settings: {
          isActive: values.settings?.isActive ?? true,
          serviceRadius: values.settings?.serviceRadius || 10,
          autoRespondToLeads: values.settings?.autoRespondToLeads ?? false,
          autoResponseMessage: values.settings?.autoResponseMessage,
        }
      };

      await dispatch(updateStore({ storeId: id, storeData })).unwrap();
      message.success('Store updated successfully!');
      navigate('/stores');
    } catch (error: any) {
      console.error('Error updating store:', error);
      message.error(error.message || 'Failed to update store');
    } finally {
      setLoading(false);
    }
  };

  const businessTypes = [
    { value: 'individual', label: 'Individual' },
    { value: 'proprietorship', label: 'Proprietorship' },
    { value: 'partnership', label: 'Partnership' },
    { value: 'llp', label: 'LLP' },
    { value: 'pvt_ltd', label: 'Private Limited' },
    { value: 'other', label: 'Other' },
  ];

  const indianStates = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
    'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
    'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
    'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
    'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli',
    'Daman and Diu', 'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry'
  ];

  const headerActions = (
    <Space wrap size={8} className="w-full min-[480px]:w-auto">
      <Button
        type="default"
        icon={<ArrowLeftOutlined />}
        className="w-full min-[480px]:w-auto border-slate-200 text-slate-700 hover:border-[#0171BF] hover:!text-[#0171BF]"
        onClick={() => navigate('/stores')}
      >
        My stores
      </Button>
    </Space>
  );

  if (storeLoading) {
    return (
      <PageShell
        title="Edit store"
        description="Loading your storefront so you can update it."
        titleEnd={headerActions}
      >
        <Card className="vendor-surface-card w-full border border-slate-200/80 shadow-sm rounded-xl">
          <div className="flex justify-center py-16">
            <Spin size="large" />
          </div>
        </Card>
      </PageShell>
    );
  }

  return (
    <PageShell
      title="Edit store"
      description="Update how this storefront appears in your account. Changes may need a quick review before they show to buyers."
      titleEnd={headerActions}
    >
      <Card className="vendor-surface-card w-full border border-slate-200/80 shadow-sm rounded-xl">
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          className="vendor-comfort-form w-full max-w-full"
          requiredMark="optional"
        >
          {/* Basic Store Information */}
          <Divider orientation="left" plain className="border-slate-200">
            <Text strong className="text-slate-800">
              Basic store information
            </Text>
          </Divider>
          
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="storeName"
                label="Store Name"
                rules={[{ required: true, message: 'Please enter store name' }]}
              >
                <Input size="large" className="w-full" placeholder="Enter store name" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="tagline"
                label="Tagline"
                rules={[{ max: 150, message: 'Tagline cannot exceed 150 characters' }]}
              >
                <Input size="large" className="w-full" placeholder="Enter store tagline" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="Store Description"
            rules={[{ required: true, message: 'Please enter description' }]}
          >
            <TextArea size="large" className="w-full" rows={4} placeholder="Describe your store" />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="categories"
                label="Categories"
                rules={[{ required: true, message: 'Please select at least one category' }]}
              >
                <Select
                  size="large"
                  className="w-full"
                  mode="multiple"
                  placeholder={categoriesLoading ? 'Loading categories...' : 'Select categories'}
                  showSearch
                  loading={categoriesLoading}
                  filterOption={(input, option) =>
                    String(option?.children).toLowerCase().includes(input.toLowerCase())
                  }
                  notFoundContent={categoriesLoading ? <Spin size="small" /> : 'No categories found'}
                >
                  {categories.map((category) => (
                    <Option key={category._id} value={category._id}>
                      {category.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="logo"
                label="Store Logo"
              >
                <Upload
                  listType="picture-card"
                  maxCount={1}
                  beforeUpload={handleLogoUpload}
                  onRemove={() => {
                    setLogoUrl('');
                    return true;
                  }}
                  fileList={logoUrl ? [{
                    uid: '1',
                    name: 'logo',
                    status: 'done',
                    url: logoUrl
                  }] : []}
                >
                  {!logoUrl && (
                    <div>
                      <UploadOutlined />
                      <div style={{ marginTop: 8 }}>Upload Logo</div>
                    </div>
                  )}
                </Upload>
              </Form.Item>
            </Col>
          </Row>

          {/* Business Information */}
          <Divider orientation="left" plain className="border-slate-200">
            <Text strong className="text-slate-800">
              Business information
            </Text>
          </Divider>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name={['businessInfo', 'businessType']}
                label="Business Type"
                rules={[{ required: true, message: 'Please select business type' }]}
              >
                <Select
                  size="large"
                  className="w-full"
                  placeholder="Select business type"
                  showSearch
                  filterOption={(input, option) =>
                    String(option?.children ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  {businessTypes.map((type) => (
                    <Option key={type.value} value={type.value}>
                      {type.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name={['businessInfo', 'phone']}
                label="Business Phone"
                rules={[
                  { required: true, message: 'Please enter business phone' },
                  { pattern: /^[6-9]\d{9}$/, message: 'Please enter a valid 10-digit phone number' }
                ]}
              >
                <Input
                  size="large"
                  className="w-full"
                  placeholder="Enter business phone number"
                  maxLength={10}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name={['businessInfo', 'email']}
                label="Business Email"
                rules={[
                  { required: true, message: 'Please enter business email' },
                  { type: 'email', message: 'Please enter valid email' },
                ]}
              >
                <Input size="large" className="w-full" placeholder="Enter business email" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name={['businessInfo', 'website']}
                label="Website"
                rules={[
                  { type: 'url', message: 'Please enter a valid website URL' }
                ]}
              >
                <Input size="large" className="w-full" placeholder="Enter website URL" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name={['businessInfo', 'yearEstablished']}
                label="Year Established"
                rules={[
                  { type: 'number', min: 1900, max: new Date().getFullYear(), message: 'Please enter a valid year' }
                ]}
              >
                <InputNumber
                  size="large"
                  className="w-full !max-w-full"
                  placeholder="Enter year established"
                  min={1900}
                  max={new Date().getFullYear()}
                  precision={0}
                  controls={false}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name={['businessInfo', 'registrationNumber']}
                label="Registration Number"
                rules={[
                  { min: 5, max: 50, message: 'Registration number must be between 5 and 50 characters' }
                ]}
              >
                <Input size="large" className="w-full" placeholder="Enter registration number" />
              </Form.Item>
            </Col>
          </Row>

          {/* Store Address */}
          <Divider orientation="left" plain className="border-slate-200">
            <Text strong className="text-slate-800">
              Store address
            </Text>
          </Divider>

          <Form.Item
            name={['address', 'street']}
            label="Street Address"
            rules={[{ required: true, message: 'Please enter street address' }]}
          >
            <Input size="large" className="w-full" placeholder="Enter street address" />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item
                name={['address', 'city']}
                label="City"
                rules={[{ required: true, message: 'Please enter city' }]}
              >
                <Input size="large" className="w-full" placeholder="Enter city" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name={['address', 'state']}
                label="State"
                rules={[{ required: true, message: 'Please select state' }]}
              >
                <Select
                  size="large"
                  className="w-full"
                  placeholder="Select state"
                  showSearch
                  filterOption={(input, option) =>
                    String(option?.children).toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {indianStates.map((state) => (
                    <Option key={state} value={state}>
                      {state}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name={['address', 'zipCode']}
                label="ZIP Code"
                rules={[
                  { required: true, message: 'Please enter ZIP code' },
                  { pattern: /^\d{6}$/, message: 'Please enter a valid 6-digit ZIP code' }
                ]}
              >
                <Input size="large" className="w-full" placeholder="Enter ZIP code" maxLength={6} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name={['address', 'landmark']}
            label="Landmark"
            rules={[
              { max: 100, message: 'Landmark cannot exceed 100 characters' }
            ]}
          >
            <Input size="large" className="w-full" placeholder="Enter landmark (optional)" />
          </Form.Item>

          {/* Store Settings */}
          <Divider orientation="left" plain className="border-slate-200">
            <Text strong className="text-slate-800">
              Store settings
            </Text>
          </Divider>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name={['settings', 'serviceRadius']}
                label="Service Radius (km)"
                rules={[
                  { type: 'number', min: 1, max: 100, message: 'Service radius must be between 1 and 100 km' }
                ]}
              >
                <InputNumber
                  size="large"
                  className="w-full !max-w-full"
                  placeholder="Enter service radius"
                  min={1}
                  max={100}
                  precision={0}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name={['settings', 'autoResponseMessage']}
                label="Auto Response Message"
                rules={[
                  { max: 500, message: 'Auto response message cannot exceed 500 characters' }
                ]}
              >
                <TextArea
                  size="large"
                  className="w-full"
                  rows={3}
                  placeholder="Enter auto response message for leads"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item className="mb-0 mt-2">
            <div className="flex w-full flex-col-reverse gap-3 pt-4 sm:flex-row sm:flex-wrap sm:justify-end sm:pt-6">
              <Button
                onClick={() => navigate('/stores')}
                size="large"
                className="w-full min-[480px]:w-auto border-slate-200 text-slate-700 hover:border-[#0171BF] hover:!text-[#0171BF]"
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                size="large"
                className="w-full min-[480px]:w-auto border-0 bg-gradient-to-r from-[#0171BF] to-[#F7941F] !text-white hover:!text-white shadow-md transition-shadow hover:shadow-lg"
              >
                Update store
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Card>
    </PageShell>
  );
};

export default EditStore;